import { ProductSlugs } from '@deque/billing-utils';
import {
  Checkbox,
  Icon,
  Select,
  type SelectOption,
  TooltipTabstop
} from '@deque/cauldron-react';
import React from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useProducts } from '~/common/contexts/products';
import styles from './Configuration.css';

export type OptionWithValueOfType<T> = Array<
  SelectOption & { value: T; disableSelectOptionOnly?: boolean }
>;

export interface Props<T extends FieldValues> {
  label: string;
  name: FieldPath<T>;
  id: string;
  allowChangeName?: FieldPath<T>;
  allowChangeProductNames?: string;
  shouldShowAllowChange: boolean;
  disabled: boolean;
  formControl: Control<T>;
  options: SelectOption[];
  tooltip?: React.ReactNode;
  selectDescription?: React.ReactNode;
}

const SelectSetting = <T extends FieldValues>({
  formControl,
  label,
  name,
  id,
  allowChangeName,
  allowChangeProductNames,
  shouldShowAllowChange,
  disabled,
  options,
  tooltip,
  selectDescription
}: Props<T>) => {
  const { getProductBySlug } = useProducts();
  const { t } = useTranslation();

  const productName =
    allowChangeProductNames ??
    getProductBySlug(ProductSlugs.axeDevToolsExtension)?.name;

  return (
    <fieldset>
      <div className={styles.selectSettingsContainer}>
        <Controller
          name={name}
          control={formControl}
          render={({ field }) => (
            <Select
              {...field}
              id={id}
              label={label}
              options={options}
              disabled={disabled}
              description={selectDescription}
            />
          )}
        />
        {tooltip && (
          <TooltipTabstop
            tooltip={tooltip}
            placement="top"
            variant="info"
            className={styles.tooltip}
          >
            <Icon
              type="question-circle"
              label={t('{{settingName}} help', { settingName: label })}
            />
          </TooltipTabstop>
        )}
      </div>
      {shouldShowAllowChange && allowChangeName && (
        <Controller
          control={formControl}
          name={allowChangeName}
          render={({ field: { value, ...field } }) => (
            <Checkbox
              {...field}
              checked={value}
              id={`allow-users-to-change-${id}`}
              label={t('Allow users to change {{label}} in {{productName}}', {
                label,
                productName
              })}
              disabled={disabled}
            />
          )}
        />
      )}
    </fieldset>
  );
};

export default SelectSetting;
